import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BlogCard from './components/BlogCard';
import CategoryCards from './components/CategoryCards';
import FeaturedArticles from './components/FeaturedArticles';
import TrendingSidebar from './components/TrendingSidebar';
import { api } from '@/lib/api';
import { adaptArticleCategory, adaptArticlePreview, type ArticleCategoryCard, type ArticlePost } from '@/lib/articles';

const SORTS = [
  { label: '最新發布', value: 'date' },
  { label: '最多閱讀', value: 'views' },
  { label: '最多按讚', value: 'likes' }
];

const BlogPage = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const [search, setSearch] = useState(initialSearch);
  const [activeCategory, setActiveCategory] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [posts, setPosts] = useState<ArticlePost[]>([]);
  const [categories, setCategories] = useState<ArticleCategoryCard[]>([]);
  const [subEmail, setSubEmail] = useState('');
  const [subStatus, setSubStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subError, setSubError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void Promise.all([
      api.getWebsiteArticleCategories(),
      api.searchWebsiteArticles({ pageCurrent: 1, pageSize: 100, sortBy: 'date' })
    ]).then(([categoryList, articlePage]) => {
      setCategories(categoryList.map(adaptArticleCategory));
      setPosts(articlePage.list.map(adaptArticlePreview));
    }).finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!subEmail.trim()) {
      setSubError('請輸入信箱地址');
      return;
    }
    const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailReg.test(subEmail)) {
      setSubError('信箱格式不正確');
      return;
    }
    setSubStatus('loading');
    setSubError('');
    try {
      await api.subscribeWebsite({ email: subEmail, source: 'blog' });
      setSubStatus('success');
      setSubEmail('');
    } catch (error) {
      setSubStatus('error');
      setSubError(error instanceof Error ? error.message : '提交失敗，請稍後再試');
    }
  };

  const featured = useMemo(() => posts.filter((post) => post.featured).slice(0, 3), [posts]);
  const trending = useMemo(() => [...posts].sort((a, b) => b.views - a.views).slice(0, 4), [posts]);
  const totalViews = useMemo(() => posts.reduce((sum, post) => sum + post.views, 0), [posts]);
  const isFiltering = search.trim() !== '' || activeCategory !== '';

  const filtered = useMemo(() => {
    let list = [...posts];
    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query) ||
          post.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          post.author.toLowerCase().includes(query)
      );
    }
    if (activeCategory) {
      list = list.filter((post) => post.category === activeCategory);
    }
    list.sort((a, b) => {
      if (sortBy === 'views') return b.views - a.views;
      if (sortBy === 'likes') return b.likes - a.likes;
      return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
    });
    return list;
  }, [activeCategory, posts, search, sortBy]);

  return (
    <div className="min-h-screen bg-[#F7F8FB]" style={{ fontFamily: "'Inter', 'DM Sans', sans-serif" }}>
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <div className="bg-[#0A1628] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[600px] h-[300px] bg-gradient-to-bl from-indigo-900/50 to-transparent" />
            <div className="absolute bottom-0 left-0 w-96 h-48 bg-gradient-to-tr from-slate-800/60 to-transparent" />
          </div>
          <div className="max-w-7xl mx-auto px-6 py-10 relative flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-xs text-slate-400 font-medium tracking-widest uppercase">NexusGo 資源中心</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-black text-white leading-tight mb-3">
                頂尖導師親筆
                <br />
                <span className="text-slate-400">AI 職場洞察</span>
              </h1>
              <p className="text-slate-400 text-sm leading-relaxed max-w-md">
                來自一線導師與行業專家的真實經驗分享，涵蓋求職、技術學習與產業趨勢。
              </p>
              <div className="flex items-center gap-6 mt-5 pt-5 border-t border-white/10">
                {[
                  { val: `${posts.length}+`, label: '篇專業文章' },
                  { val: `${Math.max(0, Math.round(totalViews / 10000))}萬+`, label: '次累計閱讀' },
                  { val: `${categories.length}`, label: '大內容分類' }
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xl font-black text-white">{item.val}</p>
                    <p className="text-slate-400 text-[11px] mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-[340px] flex-shrink-0">
              <div className="bg-white/8 border border-white/12 rounded-2xl p-5 backdrop-blur-sm">
                <p className="text-xs text-slate-400 font-medium mb-3">搜尋文章</p>
                <div className="relative mb-3">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                    <i className="ri-search-line text-slate-400 text-sm" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="文章標題、作者、標籤..."
                    className="w-full pl-10 pr-9 py-3 bg-white/12 text-white placeholder-slate-500 rounded-xl border border-white/15 focus:outline-none focus:border-white/30 text-xs transition-colors"
                  />
                  {search ? (
                    <button
                      onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-slate-500 hover:text-white w-4 h-4 flex items-center justify-center"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(new Set(posts.flatMap((post) => post.tags))).slice(0, 5).map((keyword) => (
                    <button
                      key={keyword}
                      onClick={() => setSearch(keyword)}
                      className="px-2.5 py-1 bg-white/8 hover:bg-white/15 text-white/60 hover:text-white text-[11px] rounded-full cursor-pointer transition-colors whitespace-nowrap border border-white/10"
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <CategoryCards categories={categories} activeCategory={activeCategory} onSelect={(category) => { setActiveCategory(category === activeCategory ? '' : category); setSearch(''); }} />

        {!isFiltering ? <FeaturedArticles posts={featured} /> : null}

        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm text-slate-500">
                {isFiltering ? (
                  <>
                    找到 <span className="font-bold text-[#0A1628]">{filtered.length}</span> 篇文章
                    {activeCategory ? (
                      <span className="ml-1.5 px-2 py-0.5 bg-[#0A1628] text-white text-[11px] rounded-full font-semibold">{activeCategory}</span>
                    ) : null}
                  </>
                ) : (
                  <>全部 <span className="font-bold text-[#0A1628]">{posts.length}</span> 篇文章</>
                )}
              </p>
              {isFiltering ? (
                <button
                  onClick={() => { setActiveCategory(''); setSearch(''); }}
                  className="text-xs text-rose-500 font-medium cursor-pointer flex items-center gap-1 hover:text-rose-600 whitespace-nowrap transition-colors"
                >
                  <i className="ri-close-circle-line" />清除篩選
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400">排序：</span>
              <div className="flex gap-1 bg-white border border-slate-100 rounded-full p-0.5">
                {SORTS.map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setSortBy(item.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${sortBy === item.value ? 'bg-[#0A1628] text-white' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-7">
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
                      <div className="h-44 animate-pulse bg-slate-100" />
                      <div className="space-y-3 p-5">
                        <div className="h-4 w-3/4 animate-pulse rounded bg-slate-100" />
                        <div className="h-3 w-full animate-pulse rounded bg-slate-100" />
                        <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-24 bg-white rounded-2xl border border-slate-100">
                  <div className="w-14 h-14 flex items-center justify-center bg-slate-50 rounded-full mx-auto mb-4">
                    <i className="ri-article-line text-slate-400 text-2xl" />
                  </div>
                  <p className="text-slate-500 text-sm font-semibold mb-1">沒有找到相關文章</p>
                  <p className="text-slate-400 text-xs">換個關鍵字或分類試試</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filtered.map((post) => (
                    <BlogCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </div>

            <TrendingSidebar posts={trending} />
          </div>
        </div>

        <div className="bg-[#0A1628] py-14">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl mx-auto mb-4">
              <i className="ri-mail-send-line text-white text-lg" />
            </div>
            <h3 className="text-2xl font-black text-white mb-2">每週精選，直達收件匣</h3>
            <p className="text-slate-400 text-sm mb-7">本週最值得讀的 AI 職場內容，持續更新</p>

            {subStatus === 'success' ? (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-500/20 rounded-full">
                  <i className="ri-check-line text-emerald-400 text-xl" />
                </div>
                <p className="text-emerald-400 font-bold text-sm">訂閱成功！</p>
                <p className="text-slate-400 text-xs">下期週報將傳送到你的信箱</p>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  name="email"
                  value={subEmail}
                  onChange={(event) => { setSubEmail(event.target.value); setSubError(''); }}
                  placeholder="輸入你的信箱地址"
                  className="flex-1 px-4 py-3 bg-white/10 text-white placeholder-slate-500 rounded-xl border border-white/15 focus:outline-none focus:border-white/30 text-sm transition-colors"
                />
                <button
                  type="submit"
                  disabled={subStatus === 'loading'}
                  className="px-5 py-3 bg-white hover:bg-slate-100 text-[#0A1628] text-sm font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {subStatus === 'loading' ? <><i className="ri-loader-4-line animate-spin" />訂閱中...</> : '立即訂閱'}
                </button>
              </form>
            )}

            {subError ? <p className="text-red-400 text-xs mt-2">{subError}</p> : null}
            {subStatus !== 'success' ? <p className="text-slate-600 text-xs mt-3">隨時可取消 · 不發垃圾郵件</p> : null}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogPage;
